import { Component, Input, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  PendingMembershipAlternative,
  PendingMembershipCharge,
  PendingMembershipLevel
} from '../../pending-membership/pending-membership.types';
import { formatDateToReadableString } from '../../utils/formatting';

/**
 * DEV-11022 — last stop before a card is saved on an account that picked a paid
 * membership and never paid for it. Shows what the charge will be and lets the
 * customer take a free level instead, so the membership can never start without an
 * explicit choice. Closes with the chosen option, or `null` for "Not now".
 */
@Component({
  selector: 'app-confirm-pending-membership',
  imports: [CommonModule],
  templateUrl: './confirm-pending-membership.component.html',
  styleUrl: './confirm-pending-membership.component.scss'
})
export class ConfirmPendingMembershipComponent {

  #activeModal = inject(NgbActiveModal);

  @Input()
  set pendingCharge(value: PendingMembershipCharge | undefined) {
    if (!value) return;
    this.#pendingCharge.set(value);
    // With no free level to move to there is nothing to choose between: the button
    // itself is the explicit action. Whenever there are alternatives the paid level
    // stays unselected on purpose — picking it has to be a decision.
    if (!value.alternatives?.length && value.membership)
      this.selectedId.set(value.membership.id);
  }

  #pendingCharge = signal<PendingMembershipCharge | null>(null);

  selectedId = signal<number | null>(null);

  membership = computed(() => this.#pendingCharge()?.membership ?? null);
  alternatives = computed(() => this.#pendingCharge()?.alternatives ?? []);
  otherDueBalance = computed(() => this.#pendingCharge()?.otherDueBalance ?? 0);

  /** "April 29th, 2026" — the day the level was picked, which is the part the
   *  customer no longer remembers. */
  selectedOn = computed(() => {
    const selectedOn = this.#pendingCharge()?.selectedOn;
    return selectedOn ? formatDateToReadableString(selectedOn).readableString : '';
  });

  selectedAlternative = computed(() =>
    this.alternatives().find(alternative => alternative.id === this.selectedId()) ?? null
  );

  /** What saving the card charges with the current selection. Before anything is
   *  picked it shows the pending membership's total, which is what confirming does. */
  total = computed(() =>
    this.selectedAlternative()?.totalToCharge ?? this.#pendingCharge()?.totalToCharge ?? 0
  );

  isFreeSelected = computed(() => !!this.selectedAlternative()?.isFree);

  hasSelection = computed(() => this.selectedId() !== null);

  confirmButtonText = computed(() => this.isFreeSelected() ? 'Continue' : 'Confirm & continue');

  /** Renewal wording per level. Unmapped labels fall back to the phrasing the rest
   *  of the app uses for memberships, and an empty one hides the line entirely. */
  renewalLabel(level: PendingMembershipLevel | PendingMembershipAlternative): string {
    const renewalType = level.renewalType?.toLowerCase().trim() ?? '';
    const labels: Record<string, string> = {
      annual: 'Renews every year',
      annually: 'Renews every year',
      yearly: 'Renews every year',
      monthly: 'Renews every month',
      weekly: 'Renews every week',
      quarterly: 'Renews every quarter'
    };
    return labels[renewalType] ?? (renewalType ? `Renews ${renewalType}` : '');
  }

  select(id: number) {
    this.selectedId.set(id);
  }

  confirm() {
    const selectedId = this.selectedId();
    if (selectedId === null) return;
    this.#activeModal.close({ feeLevelId: selectedId, expectedAmount: this.total() });
  }

  notNow() {
    this.#activeModal.close(null);
  }
}
